/**
 * CommHubScreen — hub unifié des communications (emails, appels, requêtes).
 * Tabs + liste + panneau de détail.
 */
import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { Mail, Phone, FileText, AlertTriangle, Plus } from 'lucide-react';
import { UniqueButton } from '@components/UniqueButton';
import { UniqueBadge } from '@components/UniqueBadge';
import { UpgradeBanner } from '@components/subscription/UpgradeGate';
import { usePlan } from '@/hooks/usePlan';

type Tab = 'all' | 'email' | 'call' | 'request';
type Status = 'new' | 'in_progress' | 'resolved' | 'escalated';

interface CommItem {
  id: string;
  type: 'email' | 'call' | 'request';
  subject: string;
  from: string;
  excerpt: string;
  date: string;
  status: Status;
}

const DEMO_ITEMS: CommItem[] = [
  {
    id: 'c1',
    type: 'email',
    subject: 'Validation budget Q2',
    from: 'daf@entreprise.bf',
    excerpt: 'Merci de valider la version v3 avant 16h…',
    date: 'Aujourd\'hui, 09:12',
    status: 'new',
  },
  {
    id: 'c2',
    type: 'call',
    subject: 'Appel manqué — Partenaire logistique',
    from: '+226 70 12 34 56',
    excerpt: '2 appels, dernier à 08:47',
    date: 'Aujourd\'hui, 08:47',
    status: 'new',
  },
  {
    id: 'c3',
    type: 'request',
    subject: 'Demande de rapport mensuel',
    from: 'DG',
    excerpt: 'À livrer pour vendredi 18h.',
    date: 'Hier, 17:20',
    status: 'in_progress',
  },
  {
    id: 'c4',
    type: 'email',
    subject: 'Relance fournisseur',
    from: 'achats@entreprise.bf',
    excerpt: 'Facture n°4591 en attente…',
    date: 'Hier, 15:08',
    status: 'escalated',
  },
  {
    id: 'c5',
    type: 'request',
    subject: 'Onboarding nouveau stagiaire',
    from: 'RH',
    excerpt: 'Compte à créer + badge.',
    date: 'Lundi, 10:00',
    status: 'resolved',
  },
];

const STATUS_TONE: Record<Status, 'gold' | 'important' | 'normal' | 'urgent'> = {
  new: 'gold',
  in_progress: 'important',
  resolved: 'normal',
  escalated: 'urgent',
};

const STATUS_LABEL: Record<Status, string> = {
  new: 'Nouveau',
  in_progress: 'En cours',
  resolved: 'Résolu',
  escalated: 'Escaladé',
};

export default function CommHubScreen(): JSX.Element {
  const { limits } = usePlan();
  const [tab, setTab] = useState<Tab>('all');
  const [selectedId, setSelectedId] = useState<string>(DEMO_ITEMS[0]!.id);

  const visible = useMemo(
    () =>
      DEMO_ITEMS.filter((i) =>
        tab === 'all' ? true : tab === 'request' ? i.type === 'request' : i.type === tab,
      ),
    [tab],
  );

  const selected = DEMO_ITEMS.find((i) => i.id === selectedId) ?? visible[0];
  const escalated = DEMO_ITEMS.filter((i) => i.status === 'escalated');

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1>Communication Hub</h1>
          <p className="mt-2 text-sm text-[color:var(--color-text-medium)]">
            Vue unifiée de vos emails, appels et requêtes.
          </p>
        </div>
        <UniqueButton
          leftIcon={<Plus size={16} />}
          disabled={!limits.commHubActions}
          title={!limits.commHubActions ? 'Disponible à partir du plan Essentiel' : undefined}
        >
          Créer une requête
        </UniqueButton>
      </div>

      {!limits.commHubActions && (
        <UpgradeBanner
          message="Actions CommHub (répondre, créer tâche, archiver) verrouillées."
          requiredPlan="essential"
        />
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        {(
          [
            ['all', 'Tous'],
            ['email', 'Emails'],
            ['call', 'Appels'],
            ['request', 'Requêtes'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className="px-4 py-2 rounded-full text-sm font-semibold transition-unique"
            style={{
              background: tab === id ? 'var(--color-primary)' : 'var(--color-white)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-text-dark)',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 flex flex-col gap-2">
          {visible.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setSelectedId(item.id)}
              className="unique-card p-4 text-left flex items-start gap-3"
              style={{
                borderColor:
                  item.id === selectedId
                    ? 'var(--color-primary)'
                    : 'var(--color-border)',
                boxShadow:
                  item.id === selectedId ? 'var(--shadow-hover)' : 'var(--shadow-card)',
              }}
            >
              <span
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  background: 'var(--color-primary-light)',
                  color: 'var(--color-primary-dark)',
                }}
              >
                {item.type === 'email' ? (
                  <Mail size={16} />
                ) : item.type === 'call' ? (
                  <Phone size={16} />
                ) : (
                  <FileText size={16} />
                )}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-sm truncate">{item.subject}</p>
                  <UniqueBadge tone={STATUS_TONE[item.status]}>
                    {STATUS_LABEL[item.status]}
                  </UniqueBadge>
                </div>
                <p className="text-xs text-[color:var(--color-text-medium)] truncate">
                  {item.from} — {item.excerpt}
                </p>
                <p className="text-[10px] text-[color:var(--color-text-medium)] mt-1">
                  {item.date}
                </p>
              </div>
            </button>
          ))}
        </div>

        <aside className="unique-card p-5">
          {selected ? (
            <>
              <UniqueBadge tone={STATUS_TONE[selected.status]}>
                {STATUS_LABEL[selected.status]}
              </UniqueBadge>
              <h3 className="text-lg font-bold mt-2">{selected.subject}</h3>
              <p className="text-xs text-[color:var(--color-text-medium)] mb-4">
                {selected.from} · {selected.date}
              </p>
              <p className="text-sm leading-relaxed">{selected.excerpt}</p>
              <div className="mt-5 flex gap-2 flex-wrap">
                <UniqueButton size="sm">Répondre</UniqueButton>
                <UniqueButton size="sm" variant="secondary">
                  Créer une tâche
                </UniqueButton>
                <UniqueButton size="sm" variant="ghost">
                  Archiver
                </UniqueButton>
              </div>
            </>
          ) : (
            <p className="text-sm text-[color:var(--color-text-medium)]">
              Sélectionnez un élément.
            </p>
          )}
        </aside>
      </div>

      {escalated.length > 0 && (
        <section className="mt-8">
          <h2 className="!text-lg mb-3 inline-flex items-center gap-2">
            <AlertTriangle size={18} color="var(--color-urgent)" />
            Escalades en cours
          </h2>
          <div className="flex flex-col gap-2">
            {escalated.map((e) => (
              <div
                key={e.id}
                className="unique-card p-4 flex items-center justify-between"
                style={{ borderLeft: '4px solid var(--color-urgent)' }}
              >
                <div>
                  <p className="font-semibold text-sm">{e.subject}</p>
                  <p className="text-xs text-[color:var(--color-text-medium)]">
                    {e.from} · {e.date}
                  </p>
                </div>
                <UniqueButton size="sm" variant="danger">
                  Escalader
                </UniqueButton>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
